import { CommonModule } from '@angular/common';
import { Component, EventEmitter, HostListener, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { ActivatedRoute } from '@angular/router';
import { filter, firstValueFrom, forkJoin, take } from 'rxjs';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { NgxExtendedPdfViewerModule } from 'ngx-extended-pdf-viewer';

import { AppService } from 'src/app/services/app.service';
import { Client, Approvalstatus } from '../../services/client.service';
import { MatDialogComponent } from 'src/app/mat-dialog/mat-dialog.component';
import { DownloadPdfService } from 'src/app/services/download-pdf.service';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { PdfCanvasViewerComponent } from "src/app/pdf-canvas-viewer/pdf-canvas-viewer.component";

@Component({
  selector: 'app-proforma-invoice-auth',
  standalone: true,
  imports: [
    MatIconModule,
    MatDialogModule,
    CommonModule,
    NgxExtendedPdfViewerModule,
    MatButtonModule,
    PdfCanvasViewerComponent
],
  templateUrl: './proforma-invoice-auth.component.html',
  styleUrls: ['./proforma-invoice-auth.component.scss']
})
export class ProformaInvoiceAuthComponent implements OnInit {

  @Input() apiResponse: any;
  @Input() pdfBase64Data: string = "";
  @Input() isPDFDataAvailable: boolean = false;
  @Output() startusChange = new EventEmitter<boolean>();

  selectedTab: string = 'proforma';
  token: string = '';
  showAuthorizeButton = true;
  selectedLanguage: string = '';
  showFullList: boolean = false;
  totalNetPrice = 0;
  totalDiscount = 0;
  totalVAT = 0;
  totalGrossPrice = 0;
  currency = '';
  isAuthorizeDisabled = false;
  Approvalstatus = Approvalstatus;
  isLargeScreen: boolean = window.innerWidth > 900;
  isDownloading = false;
  downloadCompleted = false;
  serviceJobsLength = 0;
  automaticSurchargeLines: any;

  constructor(
    public appService: AppService,
    private readonly route: ActivatedRoute,
    private readonly client: Client,
    public dialog: MatDialog,
    private readonly pdfService: DownloadPdfService,
    private sanitizer: DomSanitizer
  ) { }

  ngOnInit(): void {
    this.appService.responseData$
      .pipe(take(1))
      .subscribe(data => {
        if (!data) {
          this.loadProformaInvoiceData();
        } else {
          this.apiResponse = data;
          this.serviceJobsLength = this.apiResponse?.serviceJobs?.length || 0;
          if (this.apiResponse?.serviceLines) {
            this.automaticSurchargeLines = this.apiResponse.serviceLines.filter(
              (line: { type: string; }) => line.type === "Automatic Surcharge"
            );
          } else {
            this.automaticSurchargeLines = [];
          }
          this.calculateTotals();
          if (this.apiResponse?.approvalStatus === Approvalstatus.Accept) {
            this.isAuthorizeDisabled = true;
          }
        }
      });

    this.appService.pdfBase64Data$
      .pipe(take(1))
      .subscribe(data => {
        if (!data) {
          this.route.queryParams.pipe(take(1)).subscribe(params => {
            this.token = params['token'];
            this.getProformaInvoiceReport(this.token);
          });
        } else {
          this.pdfBase64Data = data;
          this.isPDFDataAvailable = true;
        }
      });



    this.checkScreenSize();
  }

  @HostListener('window:resize')
  onResize() {
    this.checkScreenSize();
  }

  loadProformaInvoiceData() {
    const storedLang = window.localStorage.getItem('lang');
    this.selectedLanguage = storedLang ?? 'en-US';

    this.route.queryParams.subscribe(params => {
      this.token = params['token'];
    });

    forkJoin({
      proformaInvoiceDetails: this.client.proformaDetails(this.token, this.selectedLanguage).pipe(take(1)),
    }).subscribe({
      next: ({ proformaInvoiceDetails }) => {
        this.apiResponse = proformaInvoiceDetails[0];
        if (this.apiResponse?.serviceLines) {
          this.automaticSurchargeLines = this.apiResponse.serviceLines.filter(
            (line: { type: string; }) => line.type === "Automatic Surcharge"
          );
        } else {
          this.automaticSurchargeLines = [];
        }
        this.token = this.apiResponse?.token;
        this.serviceJobsLength = this.apiResponse?.serviceJobs?.length || 0;
        if (this.apiResponse?.serviceJobs?.length) {
          if (this.apiResponse.approvalStatus === Approvalstatus.Accept) {
            this.isAuthorizeDisabled = true;
          }
          this.calculateTotals();
        }
      },
      error: (error) => {
        this.appService.handleErrorMessage(error);
      }
    });
  }

  getProformaInvoiceReport(token: string) {
    this.appService.getProformaReport(token)?.subscribe((data: string) => {
      this.pdfBase64Data = data;
      this.isPDFDataAvailable = true;
    });
  }


  ngOnChanges(changes: SimpleChanges): void {
    if (changes['apiResponse']?.currentValue) {
      this.apiResponse = changes['apiResponse'].currentValue;

      this.route.queryParams.subscribe(params => {
        this.token = params['token'];
      });

      if (this.apiResponse?.serviceJobs?.length) {
        if (this.apiResponse.approvalStatus === Approvalstatus.Accept) {
          this.isAuthorizeDisabled = true;
        }
        this.calculateTotals();
      }
    }
    if (this.apiResponse?.serviceLines) {
      this.automaticSurchargeLines = this.apiResponse.serviceLines.filter(
        (line: { type: string; }) => line.type === "Automatic Surcharge"
      );
    } else {
      this.automaticSurchargeLines = [];
    }
  }

  private calculateTotals() {
    this.totalNetPrice = 0;
    this.totalDiscount = 0;
    this.totalVAT = 0;
    this.totalGrossPrice = 0;
    this.apiResponse.serviceJobs.forEach((job: { price: any }) => {
      const price = job.price;
      this.totalNetPrice += price?.netPrice || 0;
      this.totalGrossPrice += price?.grossPrice || 0;
      this.totalDiscount += price?.discount?.amount || 0;
      if (price?.taxes?.length) {
        price.taxes.forEach((tax: { value: any }) => {
          this.totalVAT += tax?.value || 0;
        });
      }
      if (price?.currency) {
        this.currency = price.currency;
      }
    });

    this.automaticSurchargeLines.forEach((line: { price: any }) => {
      const price = line.price;
      this.totalNetPrice += price?.netPrice || 0;
      this.totalGrossPrice += price?.grossPrice || 0;

      if (price?.taxes?.length) {
        price.taxes.forEach((tax: { value: any }) => {
          this.totalVAT += tax?.value || 0;
        });
      }
    });
  }

  isObjectEmpty(obj: any): boolean {
    return Object.keys(obj).length === 0;
  }

  toggleItemList(event: Event) {
    event.preventDefault();
    this.showFullList = !this.showFullList;
  }

  async approvalConsent(status: Approvalstatus): Promise<void> {
    const body: Approvalstatus[] = [status];
    if (!body.includes(status)) return;

    const dialogType = status === Approvalstatus.Accept ? 'InvoiceAuthorized' : 'NotProvidedDisclaimer';
    const params = await firstValueFrom(this.route.queryParams);
    this.token = params['token'];

    if (!this.isAuthorizeDisabled) {
      await firstValueFrom(
        this.client.proformaConsentDetails(this.token, this.selectedLanguage, body)
      );
    }

    if (status === Approvalstatus.Accept) {
      this.isAuthorizeDisabled = true;
      this.startusChange.emit(this.isAuthorizeDisabled);
    }
    this.dialog.open(MatDialogComponent, {
      data: { type: dialogType },
    });
  }

  downloadPdf() {
    if (this.pdfBase64Data) {
      if (!this.downloadCompleted) {
        this.isDownloading = true;
      }

      this.pdfService.downloadPdf(this.pdfBase64Data, 'Proforma-Invoice.pdf', 'application/pdf')
        .then(() => {
          if (!this.downloadCompleted) {
            setTimeout(() => {
              this.isDownloading = false;
              this.downloadCompleted = true;

              this.dialog.open(MatDialogComponent, {
                data: { type: 'InvoicePdfDownload' },
              });

              setTimeout(() => {
                this.downloadCompleted = false;
              }, 5000);
            }, 1000);
          }
        })
        .catch(() => {
          this.isDownloading = false;
        });
    } else {
      this.isPDFDataAvailable = false;
    }
  }

  checkScreenSize() {
    this.isLargeScreen = window.innerWidth > 900;
  }

  jobMapping: { [key: string]: string } = {
    '=1': $localize`:@@oneJob:1 job`,
    'other': $localize`:@@multipleJobs:# jobs`
  };

}
